import * as LDClient from 'launchdarkly-js-client-sdk';
import ConfigService from 'client/util/ConfigService';
import LocalStorageService from 'client/util/LocalStorageService';
import Util from 'client/util/Util';
import Vue from 'vue';
import isEmpty from 'lodash/isEmpty';

export const featureFlags = {
  zzzFlagForTesting: 'zzzFlagForTesting',
};

/**
 * This hash maps beta feature keys used by components to feature flag names used in the feature
 * management provider. It allows for the use of more succinct names for LaunchDarkly feature flags.
 */
export const featureFlagToProviderMap = {
  [featureFlags.zzzFlagForTesting]: '2075-10-11-flag-for-testing',
};

export const featureFlagDefaultValues = {
  [featureFlags.zzzFlagForTesting]: true,
};

/**
 * The FeatureFlagModule is meant to replace using the SettingsModule when querying feature
 * flags. Currently it queries LaunchDarkly for feature flag via LaunchDarkly's javascript client.
 *
 * Additionally, FeatureFlagModule supports the browser's localStorage as a feature flag source.
 * LocalStorage driven feature flags are used to simplify local development.
 */
export function createFeatureFlagsModule(config = {}) {
  const {
    LaunchDarklyClient = LDClient,
    configService = ConfigService,
    localStorageService = LocalStorageService,
    util = Util,
  } = config;

  return {
    namespaced: true,

    state: {
      betaFeatures: {},
    },

    actions: {
      async fetchFeatureFlags({ dispatch }) {
        if (configService.launchDarklySource === 'localStorage') {
          dispatch('initLocalStorageClient');
        } else {
          await dispatch('initLaunchDarklyClient');
        }
      },

      async initLaunchDarklyClient({ commit, rootState }) {
        let ldClient;

        const processLaunchDarklySettings = (settings) => {
          Object.keys(featureFlagToProviderMap).forEach((key) => {
            const flag = featureFlagToProviderMap[key];

            const value = settings
              ? settings[flag] && settings[flag].current
              : ldClient.variation(flag, undefined);

            if (value !== undefined) {
              commit('UPDATE_BETA_FEATURES', { [key]: value });
            }
          });
        };

        return new Promise((resolve) => {
          const { launchDarklyClientSideId, launchDarklyBaseUrls } = configService;
          const { visitorId } = rootState;

          const context = {
            kind: 'user',
            key: visitorId,
          };

          const options = {
            streaming: false,
          };

          if (!isEmpty(launchDarklyBaseUrls)) {
            Object.entries(launchDarklyBaseUrls).forEach(([optionKey, optionValue]) => {
              // Only set the config values if an explicit value was given,
              // so that the default value is controlled by the LD SDK and not us.
              if (optionValue !== undefined && optionValue !== null) {
                options[optionKey] = optionValue;
              }
            });
          }
          ldClient = LaunchDarklyClient.initialize(launchDarklyClientSideId, context, options);

          ldClient.on('ready', () => {
            processLaunchDarklySettings();
            ldClient.on('change', (settings) => processLaunchDarklySettings(settings));
            resolve();
          });
        });
      },

      /**
       * When the launchDarklySource field of the GET /ui-settings.json response is set to
       * localStorage then feature flags will be read from local storage.
       *
       * Follow the steps below to use local storage as the feature flag source.
       *
       * 1. Edit /env/ui-settings.json in jumpcloud-workstation
       *
       *    diff --git a/env/ui-settings.json b/env/ui-settings.json
       *    index df424e6..5a4ad50 100644
       *    --- a/env/ui-settings.json
       *    +++ b/env/ui-settings.json
       *    @@ -2,6 +2,7 @@
       *       "deviceCertAuthCodeUrl": "https://device-cert.jumpcloud.local/auth/code",
       *       "driftScriptUrl": "https://thirdparty.jumpcloud.com/js/drift-stg.min.js",
       *       "launchDarklyClientSideId": "5eb98a3db092960a8897c0f9",
       *    +  "launchDarklySource": "localStorage",
       *       "segmentScriptUrl": "https://thirdparty.jumpcloud.com/js/segment-local.min.js",
       *       "signupFbConfigUrl": "https://thirdparty.jumpcloud.com/js/fb-config-stg.min.js",
       *       "signupFbEventsUrl": "https://thirdparty.jumpcloud.com/js/fb-events-stg.min.js",
       *
       * 2. Stop and start all workstation services.
       *
       * 3. Navigate to https://console.jumpcloud.local
       *
       * 4. Write feature flags to local storage:
       *
       *    window.localStorage.setItem('2021-03-19-some-flag', true);
       *    window.localStorage.setItem('2021-03-24-some-other-flag', true);
       */
      initLocalStorageClient({ commit }) {
        const setFeatureFlags = () => {
          try {
            Object.entries(featureFlagToProviderMap).forEach(([key, flag]) => {
              let value = localStorageService.getValue(flag);

              if (value === null) {
                value = featureFlagDefaultValues[key];
              }

              if (typeof value === 'boolean') {
                commit('UPDATE_BETA_FEATURES', { [key]: value });
              }
            });
          } catch (exp) {
            // eslint-disable-next-line
            console.error(`FeatureFlagsModule.initLocalStorageClient(): Error reading feature flags from localStorage = ${exp.toString()}`);
          }
        };

        setFeatureFlags();

        // NOTE: The storage event _must_ be triggered in another document (i.e. a new tab). The
        // event will not fire when the change to localStorage is initiated in the document where
        // the event listener was added.
        // SEE: https://developer.mozilla.org/en-US/docs/Web/API/Window/storage_event
        util.addEventListener('storage', setFeatureFlags);
      },
    },

    getters: {
      betaFeatures(state) {
        return state.betaFeatures;
      },
    },

    mutations: {
      UPDATE_BETA_FEATURES(state, payload) {
        Object.keys(payload).forEach((key) => {
          Vue.set(state.betaFeatures, key, payload[key]);
        });
      },
    },
  };
}

export default createFeatureFlagsModule();
